import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Download, Mail, Loader2, FileText, Phone, Globe, MapPin, Unlink } from "lucide-react";
import { api, mediaUrl, formatApiErrorDetail } from "@/lib/api";

const fmtSize = (b = 0) => {
  if (!b) return "";
  const u = ["B", "KB", "MB", "GB"]; let i = 0; let n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i += 1; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};

export default function SharePage() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchedFor = useRef(null);
  useEffect(() => {
    if (fetchedFor.current === shareId) return undefined;  // guard StrictMode double-mount so a visit counts once
    fetchedFor.current = shareId;
    api.get(`/public/share/${shareId}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "This link is no longer available."))
      .finally(() => setLoading(false));
    return undefined;
  }, [shareId]);

  const contact = data?.contact || {};
  const fileHref = data ? mediaUrl(data.file_url) : "";
  const downloadHref = data ? mediaUrl(data.download_url) : "";
  const mailHref = contact.email
    ? `mailto:${contact.email}?subject=${encodeURIComponent(`Enquiry about ${data?.filename || "your file"}`)}`
    : "";

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col" data-testid="share-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="mx-auto max-w-5xl px-5 py-4 flex items-center gap-3">
          {data?.logo_url
            ? <img src={mediaUrl(data.logo_url)} alt={contact.company || "Logo"} className="h-9 w-auto object-contain" data-testid="share-logo" />
            : <span className="text-lg font-semibold tracking-tight text-slate-900">{contact.company || "Dowdens Group"}</span>}
        </div>
      </header>

      {/* Main */}
      <main className="flex-1">
        <div className="mx-auto max-w-5xl px-5 py-6 sm:py-10">
          {loading && (
            <div className="flex flex-col items-center justify-center py-32 text-slate-400" data-testid="share-loading">
              <Loader2 className="h-8 w-8 animate-spin" />
              <p className="mt-3 text-sm">Loading…</p>
            </div>
          )}

          {!loading && error && (
            <div className="mx-auto max-w-md text-center py-24" data-testid="share-error">
              <div className="mx-auto h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center">
                <Unlink className="h-7 w-7 text-slate-400" />
              </div>
              <h1 className="mt-4 text-xl font-semibold text-slate-900">Link unavailable</h1>
              <p className="mt-1.5 text-sm text-slate-500">{error}</p>
              {mailHref && <a href={mailHref} className="mt-6 inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-slate-700 transition-colors"><Mail className="h-4 w-4" /> Contact us</a>}
            </div>
          )}

          {!loading && !error && data && (
            <div className="space-y-6">
              <div className="text-center sm:text-left">
                <p className="text-xs font-medium uppercase tracking-wider text-indigo-600">Shared with you</p>
                <h1 className="mt-1 text-2xl sm:text-3xl font-semibold text-slate-900 break-words" data-testid="share-title">
                  {data.asset_title || data.filename}
                </h1>
                <p className="mt-1 text-sm text-slate-500 break-words">{data.filename}{data.size ? ` · ${fmtSize(data.size)}` : ""}</p>
              </div>

              {/* Preview */}
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="share-preview">
                {data.is_image ? (
                  <div className="flex items-center justify-center bg-[repeating-conic-gradient(#f1f5f9_0_25%,#ffffff_0_50%)] bg-[length:22px_22px] p-4 sm:p-8">
                    <img src={fileHref} alt={data.filename} className="max-h-[70vh] w-auto max-w-full object-contain rounded-lg" data-testid="share-image" />
                  </div>
                ) : data.is_pdf ? (
                  <iframe title={data.filename} src={`${fileHref}#view=FitH`} className="w-full h-[70vh] sm:h-[78vh] bg-slate-100" data-testid="share-pdf" />
                ) : (
                  <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                    <FileText className="h-16 w-16" />
                    <p className="mt-3 text-sm text-slate-500">Preview not available — download to view.</p>
                  </div>
                )}
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3">
                <a href={downloadHref} download className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-indigo-600 text-white px-6 py-3.5 text-sm font-semibold hover:bg-indigo-500 transition-colors shadow-sm" data-testid="share-download-btn">
                  <Download className="h-4 w-4" /> Download File
                </a>
                {mailHref && (
                  <a href={mailHref} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white text-slate-800 px-6 py-3.5 text-sm font-semibold hover:bg-slate-100 transition-colors" data-testid="share-contact-btn">
                    <Mail className="h-4 w-4" /> Contact Sales
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-auto">
        <div className="mx-auto max-w-5xl px-5 py-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-sm text-slate-500">
          <span className="font-semibold text-slate-800">{contact.company || "Dowdens Group"}</span>
          <div className="flex flex-wrap gap-x-6 gap-y-1.5">
            {contact.phone && <a href={`tel:${contact.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 hover:text-slate-800"><Phone className="h-3.5 w-3.5" /> {contact.phone}</a>}
            {contact.email && <a href={mailHref} className="inline-flex items-center gap-1.5 hover:text-slate-800"><Mail className="h-3.5 w-3.5" /> {contact.email}</a>}
            {contact.address && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {contact.address}</span>}
            {contact.website && <a href={contact.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-slate-800"><Globe className="h-3.5 w-3.5" /> {contact.website.replace(/^https?:\/\//, "")}</a>}
          </div>
        </div>
      </footer>
    </div>
  );
}
